import AttributeValues from './attribute-values'
import Color from '@constants/color'
import ErrorBoundary from '@shared/error-boundary'
import HoursWidget from './hours-widget'
import InventoryWidget from './inventory-widget'
import MenuWidget from './menu-widget'
import OrderTable from '@shared/tables/order-table'
import Permission from '@constants/permission'
import ProfileWidget from '@shared/profile-widget'
import React, { ReactElement, useEffect, useState } from 'react'
import SimulateOrders from './simulate-orders'
import StoreForm from '@shared/forms/store-form'
import TaxRates from './tax-rates'
import TodaysInventoryWidget from './todays-inventory-widget'
import useAllActions from '@hooks/use-all-actions'
import useSelector from '@hooks/use-selector'
import { AppDispatch, OperatingState, OrderList } from '@types'
import {
  Avatar,
  CircularProgress,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Edit as EditIcon,
  EmojiTransportation,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material'
import { checkPermsAtStore, hasPerms } from '@utils/has-permission'
import { fetchAllProducts } from '@services/product-service'
import { fetchInventory as fetchInitialInventory } from '@services/initial-inventory-service'
import { fetchInventory } from '@services/todays-inventory-service'
import {
  fetchStore,
  fetchStoreOnline,
  setStoreOnline,
  updateStore,
} from '@services/store-service'
import { format12hrFrom24hr } from '@utils/date-utils'
import { getErrorMessage } from '@utils/string-utils'
import { getOrdersByStore } from '@queries/orders'
import { makeStyles } from 'tss-react/mui'
import { selectCurrentPermissionNames } from '@store/selectors'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

const Store = (): ReactElement => {
  const { classes, cx } = useStyles()
  const { storeId: storeIdParam } = useParams<{ storeId: string }>()
  const storeId = parseInt(storeIdParam as string, 10)

  const dispatch = useDispatch<AppDispatch>()
  const { setFlashMessage } = useAllActions()

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('sm'))

  const store = useSelector((state) => state.Stores.stores[storeId])
  const online = useSelector((state) => state.StoreOnline[storeId])
  const permissions = useSelector(selectCurrentPermissionNames)
  const storeIds = useSelector((state) => state.CurrentUser.user.store_ids)

  const [hasFetchedData, setHasFetchedData] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const [orders, setOrders] = useState<OrderList>([])
  const [ordersCount, setOrdersCount] = useState(0)

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        dispatch(fetchAllProducts()),
        dispatch(fetchStore(storeId)),
        dispatch(fetchStoreOnline(storeId)),
        dispatch(fetchInitialInventory(storeId)),
        dispatch(fetchInventory(storeId)),
      ]).catch((e) => setFlashMessage(getErrorMessage(e), 'error'))

      setHasFetchedData(true)
    }

    if (!hasFetchedData) {
      loadData()
    }
  }, [hasFetchedData])

  const canEditStore = checkPermsAtStore(
    permissions,
    Permission.STORES_UPDATE,
    storeIds,
    storeId,
  )
  const canEditInventory = checkPermsAtStore(
    permissions,
    Permission.STORES_UPDATE_INVENTORY,
    storeIds,
    storeId,
  )
  const canEditHours = checkPermsAtStore(
    permissions,
    Permission.STORES_UPDATE_HOURS,
    storeIds,
    storeId,
  )
  const canEditMenu = checkPermsAtStore(
    permissions,
    Permission.STORES_UPDATE_MENU,
    storeIds,
    storeId,
  )
  const canEditPars = checkPermsAtStore(
    permissions,
    Permission.STORES_UPDATE_PARS,
    storeIds,
    storeId,
  )
  const canDisableIndefinitely = checkPermsAtStore(
    permissions,
    Permission.STORES_INDEFINITELY_DISABLE_PRODUCTS,
    storeIds,
    storeId,
  )
  const canManageOfflineRestriction = checkPermsAtStore(
    permissions,
    Permission.STORES_MANAGE_OFFLINE_RESTRICTION,
    storeIds,
    storeId,
  )
  const canEditRestrictedSettings = hasPerms(Permission.STORES_CREATE)
  const canEditTaxRates = hasPerms(Permission.TAX_RATES_UPDATE)

  const fetchOrders = async (page, pageSize, begin = null, end = null) => {
    const { orders, total_results: ordersCount } = await getOrdersByStore(
      storeId,
      page,
      pageSize,
      begin,
      end,
    )
    setOrders(orders)
    setOrdersCount(ordersCount)
  }

  const handleStoreUpdate = (storeData) =>
    dispatch(updateStore(storeId, storeData))

  const handleToggleEditing = () => setIsEditing(!isEditing)

  const handleEditSubmit = (updatedStore) => {
    handleStoreUpdate(updatedStore)
      .then(() => setFlashMessage('Updated Store', 'success'))
      .catch((error) => setFlashMessage(getErrorMessage(error), 'error'))
  }

  const handleToggleStoreOnline = () => {
    dispatch(setStoreOnline(storeId, !online))
      .then(() => setFlashMessage('Store online status updated.', 'success'))
      .catch((err) => setFlashMessage(getErrorMessage(err), 'error'))
  }

  const handleToggleDriveThruOnline = () => {
    handleStoreUpdate({ ...store, drive_thru_online: !store.drive_thru_online })
      .then(() => setFlashMessage('Drive-thru status updated.', 'success'))
      .catch((err) => setFlashMessage(getErrorMessage(err), 'error'))
  }

  const driveThruIconColor = () => {
    if (!store.has_drive_thru) {
      return 'disabled'
    } else if (store.drive_thru_online) {
      return 'secondary'
    } else {
      return 'error'
    }
  }

  const routeForOrder = (id) => `/stores/${storeId}/orders/${id}`

  if (!hasFetchedData) {
    return <CircularProgress />
  }

  const buttons = () => {
    const availableButtons: any[] = [
      {
        title: 'Edit Store',
        icon: <EditIcon />,
        onClick: handleToggleEditing,
      },
    ]

    if (!canEditStore) return availableButtons

    const isOnlineToggleRestricted =
      store.restrict_offline_access && !canManageOfflineRestriction

    availableButtons.push({
      title: `Take store ${online ? 'offline' : 'online'}`,
      tooltip: isOnlineToggleRestricted
        ? 'Restricted by an administrator'
        : undefined,
      disabled: isOnlineToggleRestricted,
      icon: (
        <Avatar
          className={
            online ? classes.powerIconOnline : classes.powerIconOffline
          }
        >
          <PowerIcon />
        </Avatar>
      ),
      onClick: handleToggleStoreOnline,
    })

    if (store.has_drive_thru) {
      availableButtons.push({
        title: `Take drive-thru ${
          store.drive_thru_online ? 'offline' : 'online'
        }`,
        icon: (
          <EmojiTransportation color={driveThruIconColor()} fontSize='large' />
        ),
        onClick: handleToggleDriveThruOnline,
      })
    }

    return availableButtons
  }

  const title =
    store.name +
    (store.operating_state === OperatingState.hidden ? ' - Hidden' : '')

  return (
    <div>
      <ProfileWidget
        buttons={buttons()}
        imageUrl={store.image_url}
        subtitle1={store.address}
        subtitle2={`${store.phone}, ${
          store.email_address || 'Email address unavailable'
        }`}
        subtitle3={`Morning: Store Open, Afternoon: ${format12hrFrom24hr(
          store.afternoon_at,
        )}, Evening: ${format12hrFrom24hr(store.evening_at)}`}
        title={title}
      />
      {isEditing && (
        <div className={classes.section}>
          <StoreForm
            canEdit={canEditStore}
            canEditRestrictedSettings={canEditRestrictedSettings}
            canManageOfflineRestriction={canManageOfflineRestriction}
            onSubmit={handleEditSubmit}
            store={store}
            title='Edit Store'
          />
        </div>
      )}
      <div className={isSmallScreen ? classes.widgetRow : undefined}>
        <ErrorBoundary>
          <TodaysInventoryWidget
            className={classes.todaysInventoryWidget}
            editable={canEditInventory}
            storeId={storeId}
          />
        </ErrorBoundary>
        <HoursWidget
          className={cx(classes.widget, classes.hoursWidget)}
          editable={canEditHours}
          storeId={storeId}
          storeTimeZone={store.time_zone}
        />
      </div>
      <ErrorBoundary>
        <AttributeValues
          className={classes.section}
          editable={canEditInventory}
          storeId={storeId}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <MenuWidget
          canDisableIndefinitely={canDisableIndefinitely}
          canDisableProducts={canEditInventory}
          className={classes.widget}
          editable={canEditMenu}
          storeId={storeId}
          storeTimeZone={store.time_zone}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <InventoryWidget
          className={cx(classes.widget, classes.inventoryWidget)}
          editable={canEditPars}
          storeId={storeId}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <TaxRates
          className={classes.widget}
          editable={canEditTaxRates}
          storeId={storeId}
          title='Tax Rates and Surcharges'
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <Paper elevation={2}>
          <OrderTable
            fetchOrders={fetchOrders}
            includeCustomer
            orders={orders}
            ordersCount={ordersCount}
            routeForOrder={routeForOrder}
          />
        </Paper>
      </ErrorBoundary>
      <ErrorBoundary>
        {ENV !== 'production' && (
          <SimulateOrders canEdit={canEditStore} storeId={storeId} />
        )}
      </ErrorBoundary>
    </div>
  )
}

export default Store

const useStyles = makeStyles()((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  powerIconOnline: {
    backgroundColor: Color.onlineGreen,
  },
  powerIconOffline: {
    backgroundColor: Color.offlineRed,
  },
  widgetRow: {
    display: 'flex',
    flex: 1,
  },
  widget: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  inventoryWidget: {
    marginTop: theme.spacing(2),
  },
  todaysInventoryWidget: {
    flex: 5,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  hoursWidget: {
    flex: 2,
  },
}))
